import React, { useState, useEffect } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';

interface PricingProfileSelectorProps {
  onProfileChange: (profile: 'standard' | 'premium' | 'both') => void;
  initialProfile?: 'standard' | 'premium' | 'both';
  className?: string;
}

const PricingProfileSelector: React.FC<PricingProfileSelectorProps> = ({
  onProfileChange,
  initialProfile = 'both',
  className = ''
}) => {
  const [selectedProfile, setSelectedProfile] = useState<'standard' | 'premium' | 'both'>(initialProfile);
  const [compareMode, setCompareMode] = useState(initialProfile === 'both');

  useEffect(() => {
    // Update the selected profile when compare mode changes
    if (compareMode) {
      setSelectedProfile('both');
      onProfileChange('both');
    } else {
      // If coming out of compare mode, default to standard
      const newProfile = selectedProfile === 'both' ? 'standard' : selectedProfile;
      setSelectedProfile(newProfile);
      onProfileChange(newProfile);
    }
  }, [compareMode, onProfileChange, selectedProfile]);

  const handleProfileChange = (profile: 'standard' | 'premium') => {
    if (compareMode) {
      // If in compare mode, don't change the profile
      return;
    }
    
    setSelectedProfile(profile);
    onProfileChange(profile);
  };

  const handleCompareModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompareMode(e.target.checked);
  };

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <DynamicIcon name="tag" size={20} className="mr-2 text-blue-600" />
        Perfil de Preços
      </h3>
      
      <div className="space-y-4">
        {/* Profile Toggle */}
        <div className="flex space-x-2">
          <button
            onClick={() => handleProfileChange('standard')}
            className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
              (selectedProfile === 'standard' || selectedProfile === 'both') 
                ? 'bg-green-100 text-green-800 border border-green-300' 
                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
            }`}
            disabled={compareMode}
          >
            <div className="flex items-center justify-center">
              <DynamicIcon name="check" size={16} className={`mr-2 ${selectedProfile === 'standard' || selectedProfile === 'both' ? 'text-green-600' : 'text-gray-400'}`} />
              <span>Standard</span>
            </div>
            <div className="text-xs mt-1">Marcas convencionais</div>
          </button>
          
          <button
            onClick={() => handleProfileChange('premium')}
            className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
              (selectedProfile === 'premium' || selectedProfile === 'both') 
                ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
            }`}
            disabled={compareMode}
          >
            <div className="flex items-center justify-center">
              <DynamicIcon name="star" size={16} className={`mr-2 ${selectedProfile === 'premium' || selectedProfile === 'both' ? 'text-amber-600' : 'text-gray-400'}`} />
              <span>Premium</span>
            </div>
            <div className="text-xs mt-1">Marcas premium</div>
          </button>
        </div>
        
        {/* Compare Mode Switch */}
        <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
          <div>
            <p className="text-sm font-medium text-blue-800">Comparar ambos</p>
            <p className="text-xs text-blue-600">Mostre opções Standard e Premium lado a lado</p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={compareMode}
              onChange={handleCompareModeChange}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {/* Info Box */}
        {compareMode && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
            <p className="flex items-center">
              <DynamicIcon name="info" size={16} className="mr-2 text-blue-500" />
              Modo de comparação ativado. O orçamento mostrará ambas as opções.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingProfileSelector;